import { useMemo, useState } from "react";
import { Link } from "react-router";
import { AlertTriangle, ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Label } from "@/components/ui/label";
import { SearchInput } from "@/components/ui/search-input";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { EmptyState, ErrorState, LoadingState } from "@/components/ui/states";
import {
  agruparPorFreelancer,
  ehFinalizado,
  estaVinculado,
  periodosDisponiveis,
  propostaConflitanteDe,
  resumoDoGrupo,
} from "@/components/finance/projetos-elegiveis";
import { toneSoftClasses, toneTextClasses } from "@/lib/tones";
import { cn } from "@/lib/utils";
import { formatDateBR } from "@/utils/dateUtils";
import { formatCentsBRL } from "@/utils/money";
import { getPropostaStatusInfo } from "@/utils/proposta-status";
import { useProjetosElegiveis } from "@/hooks/use-finance-propostas";
import type { OsPossivel, ProjetoElegivel } from "@/types/finance";

interface ProjetosElegiveisPickerProps {
  agenciaId: string;
  selectedIds: string[];
  onToggle: (controleProjetoId: string) => void;
  /** Proposta atual (fluxo "Adicionar períodos"): itens já dela não contam como conflito. */
  excludePropostaId?: string;
}

const JA_TEM_OS_NO_OMIE = "Já tem OS no Omie";

function descreverOsPossivel(os: OsPossivel): string {
  const periodo = `${formatDateBR(os.periodo_inicio)}${os.periodo_fim ? ` a ${formatDateBR(os.periodo_fim)}` : ""}`;
  const valor = os.valor_cents === null ? "" : `, ${formatCentsBRL(os.valor_cents)}`;
  return `OS ${os.numero_os ?? "sem número"} (${periodo}${valor}${os.faturada ? ", faturada" : ""})`;
}

function existingProposalLabel(existente: NonNullable<ProjetoElegivel["proposta_existente"]>): string {
  const statusLabel = getPropostaStatusInfo(existente.status).label;
  return `Já está na proposta ${existente.codigo ?? "sem código"} (${statusLabel})`;
}

/**
 * Lista de períodos (`controle_projetos`) elegíveis para entrar numa proposta,
 * com cada freelancer recolhível. Reusada pela página "Nova proposta" e por
 * "Adicionar períodos" no detalhe da proposta.
 */
export function ProjetosElegiveisPicker({
  agenciaId,
  selectedIds,
  onToggle,
  excludePropostaId,
}: ProjetosElegiveisPickerProps) {
  const query = useProjetosElegiveis(agenciaId);
  const [mostrarFinalizados, setMostrarFinalizados] = useState(true);
  const [mostrarVinculados, setMostrarVinculados] = useState(false);
  const [busca, setBusca] = useState("");
  const [abertos, setAbertos] = useState<ReadonlySet<string>>(new Set());

  const disponiveis = useMemo(
    () => periodosDisponiveis(query.data?.items ?? [], excludePropostaId),
    [query.data, excludePropostaId],
  );
  const totalVinculados = disponiveis.filter((item) => estaVinculado(item, excludePropostaId)).length;
  const totalFinalizados = disponiveis.filter(
    (item) => ehFinalizado(item) && !estaVinculado(item, excludePropostaId),
  ).length;

  const filtros = { mostrarFinalizados, mostrarVinculados, busca };
  const grupos = useMemo(
    () =>
      agruparPorFreelancer(disponiveis, { mostrarFinalizados, mostrarVinculados, busca }, selectedIds, excludePropostaId),
    [disponiveis, mostrarFinalizados, mostrarVinculados, busca, selectedIds, excludePropostaId],
  );
  const sempreAberto = grupos.length === 1 || busca.trim() !== "";
  const todosAbertos = sempreAberto || grupos.every((grupo) => abertos.has(grupo.freelancer.id));

  const alternarGrupo = (freelancerId: string) => {
    setAbertos((atual) => {
      const proximo = new Set(atual);
      if (!proximo.delete(freelancerId)) proximo.add(freelancerId);
      return proximo;
    });
  };

  if (query.isLoading) return <LoadingState variant="list" count={4} />;
  if (query.isError) return <ErrorState onRetry={() => query.refetch()} />;
  if (disponiveis.length === 0) {
    return (
      <EmptyState
        title="Nenhum projeto elegível"
        description="Esta agência não tem períodos disponíveis para incluir numa proposta."
      />
    );
  }

  return (
    <div className="space-y-3">
      <SearchInput value={busca} onChange={setBusca} placeholder="Buscar por profissional, cargo ou vaga" />
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        {totalFinalizados > 0 && (
          <div className="flex items-center gap-2">
            <Switch
              id="mostrar-projetos-finalizados"
              checked={mostrarFinalizados}
              onCheckedChange={setMostrarFinalizados}
            />
            <Label htmlFor="mostrar-projetos-finalizados" className="text-xs font-normal">
              Mostrar finalizados ({totalFinalizados})
            </Label>
          </div>
        )}
        {totalVinculados > 0 && (
          <div className="flex items-center gap-2">
            <Switch
              id="mostrar-projetos-vinculados"
              checked={mostrarVinculados}
              onCheckedChange={setMostrarVinculados}
            />
            <Label htmlFor="mostrar-projetos-vinculados" className="text-xs font-normal">
              Mostrar já vinculados a uma proposta ou OS ({totalVinculados})
            </Label>
          </div>
        )}
        {!sempreAberto && grupos.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="ml-auto h-7 text-xs"
            onClick={() =>
              setAbertos(todosAbertos ? new Set() : new Set(grupos.map((grupo) => grupo.freelancer.id)))
            }
          >
            {todosAbertos ? "Recolher todos" : "Expandir todos"}
          </Button>
        )}
      </div>
      {grupos.length === 0 && (
        <p className="py-6 text-center text-sm text-muted-foreground">
          Nenhum período com estes filtros. Mude a busca ou ligue um dos filtros acima para ver os demais.
        </p>
      )}
      {grupos.map((grupo) => {
        const aberto = sempreAberto || abertos.has(grupo.freelancer.id);
        return (
          <Collapsible
            key={grupo.freelancer.id}
            open={aberto}
            onOpenChange={() => alternarGrupo(grupo.freelancer.id)}
            className="rounded-md border"
          >
            <CollapsibleTrigger
              disabled={sempreAberto}
              className="flex w-full flex-wrap items-center gap-x-3 gap-y-1 p-3 text-left hover:bg-muted/50 disabled:hover:bg-transparent"
            >
              <ChevronDown
                className={cn("h-4 w-4 shrink-0 text-muted-foreground transition-transform", !aberto && "-rotate-90")}
              />
              <span className="min-w-0 flex-1 truncate text-sm font-medium">{grupo.freelancer.nome}</span>
              {grupo.comOsPossivel > 0 && (
                <Badge variant="outline" className={cn("text-[11px] font-normal", toneSoftClasses.warning)}>
                  {grupo.comOsPossivel} {grupo.comOsPossivel > 1 ? "podem já ter OS" : "pode já ter OS"}
                </Badge>
              )}
              {grupo.selecionados > 0 && (
                <Badge variant="secondary" className="text-[11px] font-normal">
                  {grupo.selecionados} {grupo.selecionados > 1 ? "selecionados" : "selecionado"}
                </Badge>
              )}
              <span className="text-xs text-muted-foreground">{resumoDoGrupo(grupo, filtros)}</span>
            </CollapsibleTrigger>
            <CollapsibleContent className="space-y-1 px-3 pb-3">
              {grupo.periodos.map((item) => (
                <LinhaDoPeriodo
                  key={item.controle_projeto_id}
                  item={item}
                  selecionado={selectedIds.includes(item.controle_projeto_id)}
                  excludePropostaId={excludePropostaId}
                  onToggle={onToggle}
                />
              ))}
            </CollapsibleContent>
          </Collapsible>
        );
      })}
    </div>
  );
}

function LinhaDoPeriodo({
  item,
  selecionado,
  excludePropostaId,
  onToggle,
}: {
  item: ProjetoElegivel;
  selecionado: boolean;
  excludePropostaId?: string;
  onToggle: (controleProjetoId: string) => void;
}) {
  const propostaConflitante = propostaConflitanteDe(item, excludePropostaId);
  const vinculado = estaVinculado(item, excludePropostaId);
  const motivoDoBloqueio = propostaConflitante ? existingProposalLabel(propostaConflitante) : JA_TEM_OS_NO_OMIE;
  return (
    <label
      className={cn(
        "flex flex-wrap items-center gap-x-3 gap-y-1 rounded-sm p-1.5 text-sm",
        vinculado ? "opacity-60" : "cursor-pointer hover:bg-muted",
      )}
    >
      {vinculado ? (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="inline-flex">
                <Checkbox checked={false} disabled aria-label={motivoDoBloqueio} />
              </span>
            </TooltipTrigger>
            <TooltipContent className="max-w-xs text-xs">
              {propostaConflitante
                ? `${motivoDoBloqueio}. Tire o período dela para usar aqui.`
                : `${motivoDoBloqueio}, então não entra em outra proposta.`}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <Checkbox checked={selecionado} onCheckedChange={() => onToggle(item.controle_projeto_id)} />
      )}
      <span className="min-w-0 flex-1 truncate">
        {item.area_atuacao ?? "Sem cargo"}
        {item.vaga?.descricao_curta ? ` · ${item.vaga.descricao_curta}` : ""}
      </span>
      {ehFinalizado(item) && (
        <Badge variant="outline" className={cn("text-[11px] font-normal", toneSoftClasses.neutral)}>
          Finalizado
        </Badge>
      )}
      <span className="whitespace-nowrap text-xs text-muted-foreground">
        {item.data_inicio ? formatDateBR(item.data_inicio) : "sem início"}
        {" a "}
        {item.data_conclusao ? formatDateBR(item.data_conclusao) : "sem fim"}
      </span>
      <span className="whitespace-nowrap text-xs font-medium">{formatCentsBRL(item.valor_profissional_cents)}</span>
      {propostaConflitante && (
        <Link
          to={`/financeiro/propostas/${propostaConflitante.id}`}
          target="_blank"
          rel="noopener"
          className="basis-full pl-7 text-xs text-primary hover:underline"
        >
          {existingProposalLabel(propostaConflitante)}
        </Link>
      )}
      {vinculado && !propostaConflitante && (
        <span className="basis-full pl-7 text-xs text-muted-foreground">{JA_TEM_OS_NO_OMIE}</span>
      )}
      {item.os_possiveis.length > 0 && (
        <span className={cn("flex basis-full items-start gap-1 pl-7 text-xs", toneTextClasses.warning)}>
          <AlertTriangle className="mt-0.5 h-3 w-3 shrink-0" />
          <span>
            Pode já ter sido cobrado no Omie: {item.os_possiveis.map(descreverOsPossivel).join("; ")}.{" "}
            {propostaConflitante
              ? `Confira se a proposta ${propostaConflitante.codigo ?? "sem código"} não cobra em dobro.`
              : "Confira antes de incluir."}
          </span>
        </span>
      )}
    </label>
  );
}
